'use client';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { ProductWithCategories } from '@/lib/api/products';
import { formatPrice } from '@/lib/utils';

const mediaSubscription = (query: string) => (notify: () => void) => {
  const media = window.matchMedia(query);
  media.addEventListener('change', notify);
  return () => media.removeEventListener('change', notify);
};
const subscribeMobile = mediaSubscription('(max-width:650px)');
const subscribeReduced = mediaSubscription('(prefers-reduced-motion:reduce)');

export function SetDayOffers({
  products,
  onOpen,
}: {
  products: ProductWithCategories[];
  onOpen: (p: ProductWithCategories) => void;
}) {
  const mobile = useSyncExternalStore(
    subscribeMobile,
    () => window.matchMedia('(max-width:650px)').matches,
    () => false
  );
  const reduced = useSyncExternalStore(
    subscribeReduced,
    () => window.matchMedia('(prefers-reduced-motion:reduce)').matches,
    () => true
  );
  const autoplay = useMemo(
    () =>
      Autoplay({ delay: 6000, playOnInit: false, stopOnInteraction: true, stopOnMouseEnter: true }),
    []
  );
  const [ref, api] = useEmblaCarousel({ loop: products.length > 1, align: 'start' }, [autoplay]);
  useEffect(() => {
    const plugin = api?.plugins().autoplay;
    if (!plugin) return;
    if (reduced || products.length < 2) plugin.stop();
    else plugin.play();
    return () => plugin.stop();
  }, [api, reduced, products.length]);
  return (
    <div className="set-day-offers" ref={ref} aria-label="Сеты дня">
      <div className="set-day-track">
        {products.map((product) => (
          <button className="set-day-offer" key={product.id} onClick={() => onOpen(product)}>
            <picture>
              <source
                media="(max-width: 650px)"
                srcSet={
                  product.set_of_the_day_image_mobile ||
                  product.set_of_the_day_image_desktop ||
                  product.image_url
                }
              />
              <img
                src={product.set_of_the_day_image_desktop || product.image_url}
                alt={product.name}
              />
            </picture>
            {!reduced &&
              (mobile
                ? product.set_of_the_day_video_mobile
                : product.set_of_the_day_video_desktop) && (
                <video
                  src={
                    (mobile
                      ? product.set_of_the_day_video_mobile
                      : product.set_of_the_day_video_desktop) || undefined
                  }
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                />
              )}
            <span>
              <small>Сет дня</small>
              <strong>{product.name}</strong>
              <b>{formatPrice(product.price)}</b>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
